import React, { FC, useCallback, useState } from 'react';
import { FormikProps } from 'formik';
import { useIsDesktop } from '@/ui-kit/hooks/useIsDesktop';
import { useStyles } from '@/ui-kit/theme/styles';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, TextField, TypographyVariant } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import { FormError, Link, PrimaryLightButton } from '@/ui-kit/components';
import liberBlack from '@/ui-kit/theme/colors/liberBlack';
import { useReturnUrl } from '@/bo-kit/hooks/useReturnUrl';
import { LoginRequest } from '@/bo-kit/transport/types/LoginRequest';
import { LoginAndResetPassErrorCode } from '@/bo-kit/transport/types/LoginAndResetPassErrorCode';

export const SignInPageFormInner: FC<{
    formik: FormikProps<LoginRequest>;
    isError: boolean;
    error?: LoginAndResetPassErrorCode;
    errorMessage?: string;
    disabled: boolean;
    goToSignup: () => void;
}> = ({ formik, isError, error, errorMessage, disabled, goToSignup }) => {
    const isDesktop = useIsDesktop();
    const classes = useStyles();
    const { t } = useTranslation('bo-kit__signin');
    const [showPassword, setShowPassword] = useState(false);

    const handleClickShowPassword = useCallback(() => {
        setShowPassword((prev) => !prev);
    }, []);

    const handleMouseDownPassword = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    }, []);

    const [returnUrl] = useReturnUrl();

    return (
        <form onSubmit={formik.handleSubmit}>
            <Box
                sx={({ breakpoints }) => ({
                    width: isDesktop ? breakpoints.values.sm / 2 : 'unset',
                })}
            >
                <Stack spacing={2}>
                    <div>
                        <TextField
                            sx={({ spacing }) => ({ pb: spacing(3) })}
                            fullWidth
                            type='text'
                            label={t('E-mail')}
                            name='email'
                            autoComplete='username email'
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.email}
                            error={formik.touched.email && !!formik.errors.email}
                            helperText={formik.touched.email && formik.errors.email}
                            placeholder={t('Enter your e-mail')}
                        />
                        <TextField
                            fullWidth
                            variant='outlined'
                            label={t('Password')}
                            type={showPassword ? 'text' : 'password'}
                            name='password'
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.password}
                            error={formik.touched.password && !!formik.errors.password}
                            helperText={formik.touched.password && formik.errors.password}
                            placeholder={t('Enter password')}
                            autoComplete='current-password'
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position='end'>
                                        <IconButton
                                            aria-label='toggle password visibility'
                                            onClick={handleClickShowPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            edge='end'
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </div>
                    {isError && errorMessage && <FormError text={errorMessage} />}
                    <Link
                        variant={'link4' as TypographyVariant}
                        color={liberBlack[200]}
                        href='resetpassword'
                        state={{ returnUrl }}
                    >
                        {t('Forgot password?')}
                    </Link>
                    <Button
                        className={classes.btnControl}
                        type='submit'
                        variant='contained'
                        fullWidth
                        color='primary'
                        disabled={disabled}
                    >
                        {t('Log In')}
                    </Button>
                    <PrimaryLightButton variant='contained' fullWidth onClick={goToSignup}>
                        {t('Create account')}
                    </PrimaryLightButton>
                </Stack>
            </Box>
        </form>
    );
};
